//=================================================
// On the Hour
//=================================================

import { createInitializeOnce } from './utilities';
import { getReducedMotion, ignoreUserPreferences } from './user-preferences';
import { isbot } from 'isbot';

const initializeOnce = createInitializeOnce();

// Track handlers globally so we can add/remove listeners as needed
let artportMessageHandler;
let onTheHourStart;
let onTheHourEnd;
let escapeKeyHandler;

// Run any necessary project specific code outside the iframe
function projectSpecificStart(hour = new Date().getHours()) {
    // Custom behavior should ideally be passed through postMessage, and run here if present
    if (onTheHourStart !== undefined) {
        onTheHourStart();
    }
}

// Clean up any project specific code that ran outside the iframe
async function projectSpecificEnd() {
    // Custom behavior should ideally be passed through postMessage, and run here if present
    if (onTheHourEnd !== undefined) {
        onTheHourEnd();
    }
}

// Returns a Date object representing the top of the current hour
function getTopOfCurrentHour(date = new Date()) {
    const topOfCurrentHour = new Date(date);
    topOfCurrentHour.setMinutes(0, 0, 0);
    return topOfCurrentHour;
}

// Checks if the supplied time is within the first minute of the current hour
// Compare the full calendar date so a timestamp from another day at the same hour doesn't match
function isWithinFirstMinute(suppliedDate, currentDate = new Date()) {
    return suppliedDate.getFullYear() === currentDate.getFullYear() &&
        suppliedDate.getMonth() === currentDate.getMonth() &&
        suppliedDate.getDate() === currentDate.getDate() &&
        suppliedDate.getHours() === currentDate.getHours() &&
        suppliedDate.getMinutes() === 0;
}

// Calculate time difference in seconds between two dates
function timeDistance(a, b) {
    const newA = a instanceof Date ? a.getTime() : a;
    const newB = b instanceof Date ? b.getTime() : b;
    return Math.abs(Math.floor(newA / 1000) - Math.floor(newB / 1000)); // Return difference in seconds
}

// Parse input and see if it's a valid hour option
function parseHourParam(param) {
    if (!param) return null;

    const hour = parseInt(param, 10);
    // Check if it's a valid integer between 0 and 23
    if (!isNaN(hour) && hour >= 0 && hour <= 23) {
        return hour;
    }
    return null;
}

// Checks if the stored timestamp is leftover from a different "top of hour"
// ...the thought being, we don't want to override if someone is quickly changing pages during the critical minute
function isSessionVariableOutOfSync(storedDate, currentDate) {
    const topOfCurrentHour = getTopOfCurrentHour(currentDate);

    return timeDistance(storedDate, topOfCurrentHour) > 60; // Returns true if out of sync
}

// Sets a session storage variable for tracking the on-the-hour event
function setSessionVariable(currentDate) {
    const storedTimestamp = sessionStorage.getItem('onTheHour');
    const storedDate = storedTimestamp ? new Date(storedTimestamp) : null;

    // Only set a new session variable if the stored one is out of sync or doesn't exist
    if (!storedDate || isSessionVariableOutOfSync(storedDate, currentDate)) {
        sessionStorage.setItem('onTheHour', currentDate.toISOString());
    }
}

function urlWithHour(urlString, hour) {
    if (!urlString) {
        return;
    }

    const url = new URL(urlString);

    url.searchParams.set('hour', hour !== null ? hour : new Date().getHours());
    return url.toString();
}

function updateCountdown(wrapper) {
    const now = new Date();
    const currentMinute = now.getMinutes();
    const currentSecond = now.getSeconds();
    const timeEl = wrapper.querySelector('.on-the-hour-countdown__time');

    if (currentMinute === 0) {
        // Show "now" for the first minute of the hour
        timeEl.textContent = 'Now';
    } else {
        // Calculate time until next hour
        const minutesLeft = 59 - currentMinute;
        const secondsLeft = 59 - currentSecond;
        // Format the countdown
        const minuteDisplay = String(minutesLeft).padStart(2, '0');
        const secondDisplay = String(secondsLeft).padStart(2, '0');

        timeEl.textContent = `${ minuteDisplay }:${ secondDisplay }`;
    }
}

// Little text string that counts down to the next play, could be embedded on multiple pages/in HTML components
function setupOnTheHourCountdown() {
    document.querySelectorAll('.on-the-hour-countdown').forEach((wrapper) => {
        if (!initializeOnce(wrapper)) {
            return;
        }

        // Update immediately and then every second
        updateCountdown(wrapper);
        clearInterval(wrapper.countdownInterval);
        wrapper.countdownInterval = setInterval(() => {
            // Stop ticking once Turbo detaches the element
            if (!wrapper.isConnected) {
                clearInterval(wrapper.countdownInterval);
                return;
            }
            updateCountdown(wrapper);
        }, 1000);
        wrapper.style.display = 'flex';
    });
}

export default function setupOnTheHour() {
    setupOnTheHourCountdown();

    const wrapper = document.querySelector('.on-the-hour');
    if (!wrapper || !initializeOnce(wrapper)) {
        return;
    }

    const THIRTY_SECONDS = 30000;
    let hourParam = new URLSearchParams(window.location.search).get('on-the-hour');
    let forceHour = parseHourParam(hourParam);

    // If meta tag is passed OR browser is a bot disable
    const shouldDisable = isbot(navigator.userAgent) ||
        document.querySelector('meta[name="on-the-hour"]')?.getAttribute('content') === 'false' ||
        (hourParam === 'false');
    const legend = wrapper ? wrapper.querySelector('.on-the-hour-legend') : null;
    const iframe = wrapper ? wrapper.querySelector('.on-the-hour__iframe') : null;
    const forceOnTheHour = hourParam === 'true' || forceHour !== null;
    const url = wrapper?.dataset.url;
    let currentDate = new Date();

    // If there's no project to load cancel everything
    if (!url) {
        return;
    }

    function onTheHourClose() {
        // Remove escape key handler
        if (escapeKeyHandler) {
            document.removeEventListener('keydown', escapeKeyHandler);
            escapeKeyHandler = null;
        }

        // Empty the iframe
        if (iframe) {
            iframe.classList.remove('active', 'will-be-active');

            // Because of reduced motion the project may not have loaded, and if it didn't we shouldn't run it's specific cleanup
            if (iframe.src) {
                projectSpecificEnd();
                iframe.src = '';
            }
        }
        wrapper.classList?.remove('reduced-motion');

        scheduleNextCheck();
    }

    // Load in the actual iFrame for the project + any extras
    function loadProject() {
        // Load and display the iframe content
        function loadCallback() {
            iframe.classList.add('active'); // Removes visibility hidden on the project

            projectSpecificStart(); // Run any project specific code (e.g. changes to whitney.org)
            setSessionVariable(currentDate); // Update session storage with the current time
            setTimeout(() => wrapper.close(), THIRTY_SECONDS); // Let the project run for 30 seconds before closing
        }

        // If the window is backgrounded the dialog may no longer be attached to the DOM
        if (!wrapper.isConnected) {
            return;
        }

        iframe.addEventListener('load', loadCallback, { once: true });
        // If this show is in the callback the iframe can be sized incorrectly on initial load
        try {
            // Sometimes this can fail in the background
            wrapper.showModal();
        } catch (e) {
            // Keep the hourly chain alive even when the modal can't open
            scheduleNextCheck();
            return;
        }

        wrapper.focus();
        iframe.classList.add('will-be-active'); // Projects may need iframe size so first display but keep visibility hidden
        hourParam = new URLSearchParams(window.location.search).get('on-the-hour');
        forceHour = parseHourParam(hourParam);
        iframe.src = urlWithHour(url, forceHour);

        // Add escape key handler while project is running
        if (!escapeKeyHandler) {
            escapeKeyHandler = (e) => {
                if (e.key === 'Escape') {
                    wrapper.close();
                }
            };
            document.addEventListener('keydown', escapeKeyHandler);
        }

        // Analytics tracking (if enabled)
        if (useAnalytics && !forceOnTheHour) {
            dataLayer.push({
                event: 'artport',
                label: 'on_the_hour',
                value: currentDate.getHours(),
            });
        }
    }

    // Triggers the projects if conditions are met and schedules the next one
    function triggerOnTheHour(force = false) {
        // Make sure elements are present and we aren't disabling
        if (shouldDisable || !wrapper || !iframe) {
            return scheduleNextCheck();
        }

        currentDate = new Date();
        const storedTimestamp = sessionStorage.getItem('onTheHour');
        const storedDate = storedTimestamp ? new Date(storedTimestamp) : null;

        // Only trigger if forced, or if within the first minute of the hour AND:
        // - No stored date exists OR
        // - The stored date exists but is not within the first minute of the current hour
        // ...the goal is to prevent multiple rapid re-fires on refresh or link clicks
        const shouldTrigger = force ||
            (isWithinFirstMinute(currentDate, currentDate) &&
                (!storedDate || !isWithinFirstMinute(storedDate, currentDate)));


        // If no trigger, still schedule the next check
        if (!shouldTrigger) {
            return scheduleNextCheck();
        }

        // Reduced motion users get the option to trigger the project
        if (getReducedMotion() && !ignoreUserPreferences()) {
            // Show the legend
            wrapper.classList.add('reduced-motion');
            try {
                // Sometimes this can fail in the background
                wrapper.showModal();
            } catch (e) {
                // Keep the hourly chain alive even when the modal can't open
                scheduleNextCheck();
                return;
            }
            wrapper.focus();

            // Set so reduced motion modal gets treated as "seeing" the project if they navigate or close it to prevent re-shows
            setSessionVariable(currentDate); // Update session storage with the current time

            // Set a reasonable delay for someone to read and trigger the project (or not)
            setTimeout(() => {
                // If the iframe is active it means the user triggered the project
                // ...if it isn't we should remove the legend since it seems they don't want to
                if (!iframe.src) {
                    // The close event handler takes care of scheduling the next check
                    wrapper.close();
                    wrapper.classList?.remove('reduced-motion');
                }
            }, THIRTY_SECONDS);
            return;
        }

        loadProject();
    }

    // Schedules the next check based on the time until the next top of the hour
    function scheduleNextCheck() {
        const now = new Date();

        // Get the top of the current hour
        let nextTopOfHour = getTopOfCurrentHour(now);

        // If the current time is past the top of the current hour, move to the next hour
        if (now.getTime() >= nextTopOfHour.getTime()) {
            nextTopOfHour.setHours(nextTopOfHour.getHours() + 1);
        }

        // Calculate the exact time difference to the next top of the hour
        const timeUntilNextHour = nextTopOfHour.getTime() - now.getTime();

        // Set a timeout to trigger exactly at the next top of the hour
        setTimeout(() => {
            triggerOnTheHour();
        }, timeUntilNextHour);
    }

    // Expose this to the window so we can arbitrarily call it elsewhere (including signage for testing)
    window.forceOnTheHour = () => {
        // Remove existing session variable so it will for sure trigger
        sessionStorage.removeItem('onTheHour');
        triggerOnTheHour(true)
    }

    // Check for query triggers
    if (forceOnTheHour) {
        window.forceOnTheHour();
    } else {
        // Only trigger if within the first minute of the top of the hour
        if (isWithinFirstMinute(currentDate, currentDate)) {
            triggerOnTheHour();
        } else {
            scheduleNextCheck(); // If not within the first minute, just schedule the next check
        }
    }

    wrapper.addEventListener('close', () => {
        onTheHourClose();
    });

    // Close overlay if click is outside of legend when reduced motion is on
    wrapper.addEventListener('click', (e) => {
        if (wrapper.classList.contains('reduced-motion')) {
            const rect = legend.getBoundingClientRect();
            const insideOverlay = (rect.top <= e.clientY && e.clientY <= rect.top + rect.height &&
                rect.left <= e.clientX && e.clientX <= rect.left + rect.width);

            if (!insideOverlay && !e.target.matches('.on-the-hour-legend__trigger')) {
                wrapper.close();
            }
        }
    });

    // Allow for manual triggering for people with reduced motion on
    wrapper.querySelectorAll('.on-the-hour-legend__trigger').forEach((el) => {
        el.addEventListener('click', () => {
            wrapper.classList.remove('reduced-motion');
            loadProject();

            // Analytics tracking (if enabled)
            if (useAnalytics) {
                dataLayer.push({
                    event: 'artport',
                    label: 'on_the_hour_trigger',
                    value: 'true',
                });
            }
        });
    });

    // Handle clicking any close button or project link
    wrapper.querySelectorAll('.on-the-hour-legend__x, .on-the-hour-legend__link').forEach((el) => {
        el.addEventListener('click', () => {
            wrapper.close();
        });
    });

    // Allow artport to post changes to the parent window (depending on the project)
    // Remove the previous visit's handler before registering a new one so listeners don't stack
    if (artportMessageHandler) {
        window.removeEventListener('message', artportMessageHandler);
    }
    artportMessageHandler = (e) => {
        if (e.origin === 'https://artport.whitney.org' || process.env.NODE_ENV === 'development') {
            const data = e.data;
            if (data.code) {
                // Messages might need to be run at specific times (so save for later)
                // If not, then can be run immediately
                if (data.time === 'start') {
                    onTheHourStart = () => eval(data.code);
                } else if (data.time === 'end') {
                    onTheHourEnd = () => eval(data.code);
                } else {
                    eval(data.code);
                }
            }
        }
    };
    window.addEventListener('message', artportMessageHandler);
}
